import { FC, useRef, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { DataGridResendIcon } from '../../../ui';
import { TRowClickFunction } from '../../../ui/components/DataGridPro/helpers';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { DataGridProgressionField } from '../DataGridProgressionField';
import { ListContext } from '../../../ui';
import { PatientModel } from '../../network';
import { CheckCircle, Pending, Cancel } from '@mui/icons-material';
import {
  DataGridTable,
  CombinedTableModel,
} from '../../../ui/components/DataGridPro';
import {
  formatNullValues,
  formatDate,
  formatNextScanDate,
} from '../../../ui/components/DataGridPro/helpers';

interface PatientDataGridProps {
  height: string;
}

export const PatientDataGrid: FC<PatientDataGridProps> = (props) => {
  const { height } = props;
  const { data, loading } = useContext(ListContext);
  const navigate = useNavigate();
  const [selectedRowID, setSelectedRowID] = useState<number | null>(null);

  const buttonHoverRef = useRef(false);

  const handleMouseOver = () => {
    buttonHoverRef.current = true;
  };

  const handleMouseOut = () => {
    buttonHoverRef.current = false;
  };

  const onRowClick: TRowClickFunction<any> = (
    item: GridRowParams<PatientModel>
  ) => {
    setSelectedRowID(Number(item.id));
    if (!buttonHoverRef.current) {
      navigate(`/patients/${item.id}`);
    }
  };

  const renderStatusIcon = (value: boolean, type: 'invitation' | 'bracing') => {
    const titles = {
      invitation: ['Invitation accepted', 'Invitation pending'],
      bracing: [
        'Brace sensor data available',
        'Brace sensor data not available',
      ],
    };
    const icons = {
      true: <CheckCircle color='success' fontSize='small' />,
      false:
        type === 'invitation' ? (
          <Pending color='warning' fontSize='small' />
        ) : (
          <Cancel color='action' fontSize='small' />
        ),
    };
    return (
      <Tooltip title={titles[type][value ? 0 : 1]}>
        {icons[value ? 'true' : 'false']}
      </Tooltip>
    );
  };

  const columns: GridColDef<CombinedTableModel>[] = [
    {
      field: 'first_name',
      headerName: 'First Name',
      flex: 1,
      minWidth: 100,
      valueGetter: (params) => {
        return formatNullValues(params.value);
      },
    },
    {
      field: 'last_name',
      headerName: 'Last Name',
      flex: 1,
      minWidth: 100,
      valueGetter: (params) => {
        return formatNullValues(params.value);
      },
    },
    {
      field: 'coordinator_name',
      headerName: 'Coordinator',
      flex: 1,
      minWidth: 125,
    },
    {
      field: 'age',
      headerName: 'Age',
      flex: 1,
      minWidth: 50,
      valueGetter: (params) => {
        return formatNullValues(params.value);
      },
    },
    {
      field: 'cobb_angle',
      headerName: 'AI Cobb',
      flex: 1,
      minWidth: 65,
      valueGetter: (params) => {
        return formatNullValues(params.value);
      },
    },
    {
      field: 'progression_since_last_scan',
      headerName: 'Progression',
      flex: 1,
      minWidth: 85,
      valueGetter: (params) => {
        return formatNullValues(params.value);
      },
      renderCell: (params) => {
        return (
          <DataGridProgressionField
            record={params.row}
            label='Progression since last scan'
            source='progression_since_last_scan'
          />
        );
      },
    },
    {
      field: 'total_scans',
      headerName: 'Scans',
      flex: 1,
      minWidth: 55,
      valueGetter: (params) => {
        return formatNullValues(params.value);
      },
    },
    {
      field: 'most_recent_scan_date',
      headerName: 'Last scan',
      flex: 1,
      minWidth: 90,
      valueGetter: (params) => {
        return formatDate(params.value);
      },
    },
    {
      field: 'email_verified',
      headerName: 'Invitation',
      flex: 1,
      minWidth: 50,
      renderCell: (params) => {
        return renderStatusIcon(params.value, 'invitation');
      },
    },
    {
      field: 'has_brace_sensor_data',
      headerName: 'Brace Sensor',
      flex: 1,
      minWidth: 75,
      renderCell: (params) => {
        return renderStatusIcon(params.value, 'bracing');
      },
    },
    {
      field: '',
      headerName: 'Actions',
      flex: 1,
      minWidth: 75,
      sortable: false,
      renderCell: (params) => {
        const patientRow = params.row as PatientModel;
        const { isPastDate } = formatNextScanDate(
          patientRow?.next_scan_date || null
        );
        return (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <DataGridResendIcon
              label=''
              record={params.row}
              source='next_scan_date'
              onMouseOver={handleMouseOver}
              onMouseOut={handleMouseOut}
              selectedRowID={selectedRowID}
              resendtype='next_scan'
              hide={!isPastDate}
            />

            <DataGridResendIcon
              label=''
              record={params.row}
              source='email_verified'
              onMouseOver={handleMouseOver}
              onMouseOut={handleMouseOut}
              selectedRowID={selectedRowID}
              resendtype='invitation'
              hide={patientRow?.email_verified}
            />
          </div>
        );
      },
    },
  ];

  return (
    <DataGridTable
      height={height}
      loading={loading}
      data={data}
      columns={columns}
      tableType='patient'
      onRowClick={onRowClick}
    />
  );
};
